import { Injectable } from '@angular/core';
import { ofType, Actions, Effect } from '@ngrx/effects';
import { of as observableOf } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  first,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { AnonymousUser, AuthService, PersonaUser } from '®shared/auth';
import { AppConfig } from '®shared/config';
import { UserService } from '®shared/user';

import {
  currentAnonymousUser,
  currentPersonaUser,
} from './current-user-and-permissions';
import {
  CurrentUserActionTypes,
  StoreAccessTokenAction,
  StoreCurrentUserAction,
} from './current-user.actions';

@Injectable()
export class CurrentUserEffects {
  constructor(
    private actions$: Actions,
    private appConfig: AppConfig,
    private authService: AuthService,
    private userService: UserService,
  ) {}

  @Effect()
  establishCurrentUser$ = this.actions$.pipe(
    ofType(CurrentUserActionTypes.REQUEST_CURRENT_USER),
    first(),
    switchMap(() => this.authService.authorizeByUrl()),
    switchMap(oauthUser => {
      if (oauthUser.isAnonymous) {
        return observableOf(
          currentAnonymousUser(this.appConfig, oauthUser as AnonymousUser),
        );
      }
      return this.userService.loginWithTarl(oauthUser.guid).pipe(
        catchError(() => observableOf(null)),
        map(tarlUser =>
          currentPersonaUser(
            this.appConfig,
            oauthUser as PersonaUser,
            tarlUser,
          ),
        ),
      );
    }),
    map(currentUser => new StoreCurrentUserAction({ currentUser })),
  );

  @Effect({ dispatch: false })
  logInWithOAuth$ = this.actions$.pipe(
    ofType(CurrentUserActionTypes.LOG_IN),
    tap(() => this.authService.login()),
  );

  @Effect({ dispatch: false })
  logOut$ = this.actions$.pipe(
    ofType(CurrentUserActionTypes.LOG_OUT),
    tap(() => this.authService.logout()),
  );

  @Effect()
  updateAccessToken$ = this.authService.user$.pipe(
    map(user => user.accessToken),
    distinctUntilChanged(),
    map(accessToken => new StoreAccessTokenAction({ accessToken })),
  );
}
